import { useEffect } from 'react'

import useBrand from './useBrand'
import useCurrentUser from './useCurrentUser'

type IntercomType = {
  hideWidget?: boolean,
  horizontalPadding?: number,
}

export type TrackEventParams = {
  eventName: string,
  eventPayload?: Record<string, string | boolean>,
}

const useIntercom = (options: IntercomType = {}) => {
  // TODO: When the intercom flipper key is removed, default the hideWidget option to false
  const { hideWidget, horizontalPadding = 20 } = options

  function loadIntercom(appId: string, settings: Record<string, unknown> = {}) {
    if (typeof window === 'undefined') return

    if (window.Intercom && typeof window.Intercom === 'function'){
      window.Intercom('update', settings)
      return
    }

    window.intercomSettings = { app_id: appId, ...settings }

    const scriptId = 'intercom-script'
    if (document.getElementById(scriptId)) return

    const script = document.createElement('script')
    script.id = scriptId
    script.async = true
    script.src = `https://widget.intercom.io/widget/${appId}`
    document.head.appendChild(script)
  }

  const {
    currentUser: {
      brand_id, email, full_name, id, mobile_number, cache_key,
    },
    isBrandUser, agency, roleTitle, loaded,
  } = useCurrentUser()

  const { brand: brandEntity } = useBrand({ id: brand_id })

  useEffect(() => {
    const settings = {
      app_id: process.env.REACT_APP_INTERCOM_APP_ID,
      brand: brandEntity?.title,
      company: {
        id: agency?.id?.toString() || `unknown-${id}`,
        name: agency?.title || 'Unknown Company',
        industry: 'Real Estate',
        plan: 'N/A',
        user_count: 'N/A',
      },
      custom_attributes: {
        business_unit: 'Realhub',
        onboarding: false,
      },
      email,
      horizontal_padding: horizontalPadding,
      name: full_name,
      phone: mobile_number,
      role: isBrandUser ? 'Brand User' : roleTitle,
      user_id: id,
      z_index: 997,
    }

    if (!hideWidget){
      loadIntercom(process.env.REACT_APP_INTERCOM_APP_ID!, settings)
    }
  }, [cache_key, hideWidget, loaded])

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (!window.Intercom || hideWidget) return

      const target = event.target as HTMLElement | null
      if (!target) return

      const clickedInsideIntercom = !!target.closest('[id^="intercom"], [class*="intercom"]')

      if (!clickedInsideIntercom){
        window.Intercom?.('hide')
      }
    }

    document.addEventListener('click', onDocClick, { capture: true, passive: true })
    return () => document.removeEventListener('click', onDocClick, { capture: true })
  }, [hideWidget])

  const trackEvent = (params: TrackEventParams) => {
    const { eventName, eventPayload = {} } = params
    if (window.Intercom){
      const extendedPayload = {
        ...eventPayload,
        agency: agency?.title,
        business_unit: 'Engage',
        role: isBrandUser ? 'Brand User' : roleTitle,
        user_id: id || 'Unknown User',
      }
      window.Intercom('trackEvent', eventName, extendedPayload)
    }
  }

  return {
    callbacks: {
      trackEvent: (params: TrackEventParams) => {
        trackEvent(params)
      },
    },
  }
}

export default useIntercom
